/**
 * Geo calculation utilities for area, distance, and angle measurement.
 * Uses the Haversine formula and Spherical Excess for accurate Earth-surface calculations.
 */

const EARTH_RADIUS = 6371000; // meters

/** Degrees to radians */
function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Radians to degrees */
function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

export interface GeoPoint {
  lat: number;
  lng: number;
}

/**
 * Calculate distance between two geo points using the Haversine formula.
 * Returns distance in meters.
 */
export function calcDistance(p1: GeoPoint, p2: GeoPoint): number {
  const dLat = toRad(p2.lat - p1.lat);
  const dLng = toRad(p2.lng - p1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(p1.lat)) *
      Math.cos(toRad(p2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS * c;
}

/**
 * Calculate total path distance (sum of segments).
 * Returns distance in meters.
 */
export function calcTotalDistance(points: GeoPoint[]): number {
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    total += calcDistance(points[i], points[i + 1]);
  }
  return total;
}

/**
 * Calculate the area of a polygon defined by geo points.
 * Uses the Spherical Excess formula for closed polygons on Earth's surface.
 * Returns area in square meters.
 */
export function calcArea(points: GeoPoint[]): number {
  if (points.length < 3) return 0;

  // Use the Shoelace formula adapted for geographic coordinates
  let area = 0;
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const xi = toRad(points[i].lng);
    const yi = toRad(points[i].lat);
    const xj = toRad(points[j].lng);
    const yj = toRad(points[j].lat);

    area += (xj - xi) * (2 + Math.sin(yi) + Math.sin(yj));
  }

  area = Math.abs((area * EARTH_RADIUS * EARTH_RADIUS) / 2);
  return area;
}

/**
 * Calculate angle between three points (at vertex p2) in degrees.
 */
export function calcAngle(p1: GeoPoint, p2: GeoPoint, p3: GeoPoint): number {
  const bearing1 = calcBearing(p2, p1);
  const bearing2 = calcBearing(p2, p3);

  let angle = bearing2 - bearing1;
  if (angle < 0) angle += 360;
  if (angle > 180) angle = 360 - angle;

  return angle;
}

/**
 * Calculate bearing from point A to point B in degrees.
 */
function calcBearing(from: GeoPoint, to: GeoPoint): number {
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  let bearing = toDeg(Math.atan2(y, x));
  return (bearing + 360) % 360;
}

/**
 * Format distance for display.
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${meters.toFixed(1)} m`;
  }
  return `${(meters / 1000).toFixed(3)} km`;
}

/**
 * Format area for display with unit conversions.
 */
export interface AreaUnits {
  sqm: number;       // 제곱미터
  pyeong: number;    // 평
  sqkm: number;      // 제곱킬로미터
  hectare: number;   // 헥타르
  acre: number;      // 에이커
}

export function getAreaUnits(sqm: number): AreaUnits {
  return {
    sqm,
    pyeong: sqm * 0.3025,
    sqkm: sqm / 1_000_000,
    hectare: sqm / 10_000,
    acre: sqm / 4046.856,
  };
}

/**
 * Format area with auto unit selection.
 */
export function formatArea(sqm: number): string {
  return `${sqm.toLocaleString('ko-KR', { maximumFractionDigits: 1 })} m²`;
}

/**
 * Format angle for display.
 */
export function formatAngle(degrees: number): string {
  const d = Math.floor(degrees);
  const mf = (degrees - d) * 60;
  const m = Math.floor(mf);
  const s = ((mf - m) * 60).toFixed(1);
  return `${d}° ${m}' ${s}"`;
}

/**
 * Distance unit conversions
 */
export interface DistanceUnits {
  m: number;
  km: number;
  ft: number;
  mi: number;
}

export function getDistanceUnits(meters: number): DistanceUnits {
  return {
    m: meters,
    km: meters / 1000,
    ft: meters * 3.28084,
    mi: meters / 1609.344,
  };
}
